type PortalCanvasOptions = {
  imageSrc: string;
  hue?: number;
};

type PortalConfig = {
  imgSrc: string;
  hue: number;
};

type PortalParticle = {
  a: number;
  dist: number;
  size: number;
  speed: number;
  tw: number;
  tri: boolean;
  rot: number;
};

type PortalSquare = {
  a: number;
  rr: number;
  size: number;
  tw: number;
  drift: number;
};

type PortalTriangle = {
  a: number;
  rr: number;
  size: number;
  rot: number;
  tw: number;
};

type PortalData = PortalConfig & {
  img: HTMLImageElement;
  ringSquares: PortalSquare[];
  ringTriangles: PortalTriangle[];
  particles: PortalParticle[];
};

type PortalCenter = {
  x: number;
  y: number;
  phase: number;
};

export class PortalCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  private w = 0;
  private h = 0;
  private dpr = 1;
  private portalRadius = 80;
  private portalCenters: PortalCenter[] = [];
  private imageSrc: string;
  private hue: number;
  private PORTALS: PortalData[];

  private readonly CONFIG = {
    bgColor: '#212121',
    maxDpr: 1.15,
    portalThickness: 10,
    innerRadiusFactor: 0.9,
    ringSquares: 56,
    ringTriangles: 18,
    outerParticles: 22,
    portalImageOpacity: 0.95,
    portalImageScale: 1.06,
    portalImageDrift: 2.5,
    haloAlpha: 0.05,
    innerTintAlpha: 0.1,
    vignetteAlpha: 0.1,
    featherStart: 0.72,
    featherEnd: 1.0,
    lineAlpha: 0.08,
    pulseSpeed: 0.0018,
    spinSpeed: 0.00065,
    driftSpeed: 0.00055,
    topBottomPaddingFactor: 0.18,
    sidePaddingFactor: 0.05,
    gapFactor: 0.03,
  };

  constructor(canvas: HTMLCanvasElement, options: PortalCanvasOptions) {
    this.canvas = canvas;
    this.imageSrc = options.imageSrc;
    this.hue = options.hue ?? 205;

    const ctx = canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
    });

    if (!ctx) {
      throw new Error('Não foi possível obter o contexto 2D do canvas.');
    }

    this.ctx = ctx;

    this.PORTALS = [
      {
        imgSrc: this.imageSrc,
        hue: this.hue,
        img: new Image(),
        ringSquares: [],
        ringTriangles: [],
        particles: [],
      },
    ];
  }

  public init() {
    this.setupImages();
    this.resize();
    window.addEventListener('resize', this.resize, { passive: true });
    requestAnimationFrame(this.animate);
  }

  private setupImages() {
  this.PORTALS.forEach((portal) => {
      portal.img.decoding = 'async';

      portal.img.onload = () => {
        console.log('Imagem carregada');
        this.resize(); // força rebuild
      };

      portal.img.onerror = () => {
        console.error('Erro ao carregar imagem:', portal.imgSrc);
      };

      portal.img.src = portal.imgSrc;
    });
  }

  private rand(min: number, max: number) {
    return Math.random() * (max - min) + min;
  }

  private hsla(h: number, s: number, l: number, a: number) {
    return `hsla(${h}, ${s}%, ${l}%, ${a})`;
  }

  private portalColor(hue: number, alpha = 1, light = 56, hueShift = 0, sat = 92) {
    return this.hsla(hue + hueShift, sat, light, alpha);
  }

  private hotColor(hue: number, alpha = 1) {
    return this.hsla(hue - 10, 96, 68, alpha);
  }

  private getPortalRadius() {
    const sidePadding = this.w * this.CONFIG.sidePaddingFactor;
    const gap = this.w * this.CONFIG.gapFactor;
    const usableWidth = this.w - sidePadding * 2 - gap * (this.PORTALS.length - 1);
    const radiusByWidth = usableWidth / (this.PORTALS.length * 2);
    const radiusByHeight = this.h * (0.5 - this.CONFIG.topBottomPaddingFactor);

    return Math.max(24, Math.min(radiusByWidth, radiusByHeight));
  }

  private buildPortalCenters() {
    this.portalCenters = [];

    const gap = this.w * this.CONFIG.gapFactor;
    const diameter = this.portalRadius * 2;
    const totalWidth = this.PORTALS.length * diameter + (this.PORTALS.length - 1) * gap;
    const startX = (this.w - totalWidth) * 0.5 + this.portalRadius;
    const cy = this.h * 0.55;

    for (let i = 0; i < this.PORTALS.length; i++) {
      this.portalCenters.push({
        x: startX + i * (diameter + gap),
        y: cy,
        phase: i * 0.9,
      });
    }
  }

  private buildPortalData() {
    const r = this.portalRadius;
    const thickness = this.CONFIG.portalThickness;

    this.PORTALS.forEach((portal) => {
      portal.ringSquares = [];
      portal.ringTriangles = [];
      portal.particles = [];

      for (let i = 0; i < this.CONFIG.ringSquares; i++) {
        const a = (i / this.CONFIG.ringSquares) * Math.PI * 2;
        portal.ringSquares.push({
          a,
          rr: r + this.rand(-thickness, thickness),
          size: this.rand(3, 5.5),
          tw: this.rand(0, Math.PI * 2),
          drift: this.rand(-1.6, 1.6),
        });
      }

      for (let i = 0; i < this.CONFIG.ringTriangles; i++) {
        const a = (i / this.CONFIG.ringTriangles) * Math.PI * 2 + this.rand(-0.05, 0.05);
        portal.ringTriangles.push({
          a,
          rr: r + this.rand(-thickness, thickness),
          size: this.rand(4, 6.5),
          rot: this.rand(0, Math.PI * 2),
          tw: this.rand(0, Math.PI * 2),
        });
      }

      for (let i = 0; i < this.CONFIG.outerParticles; i++) {
        portal.particles.push({
          a: this.rand(0, Math.PI * 2),
          dist: r + this.rand(18, 42),
          size: this.rand(1.4, 2.8),
          speed: this.rand(0.00045, 0.0011),
          tw: this.rand(0, Math.PI * 2),
          tri: Math.random() < 0.2,
          rot: this.rand(0, Math.PI * 2),
        });
      }
    });
  }

private resize = () => {
  const size = Math.min(window.innerWidth * 0.8, 600); // controla aqui 👈

  this.dpr = Math.min(window.devicePixelRatio || 1, this.CONFIG.maxDpr);

  // tamanho visual (CSS via JS)
  this.canvas.style.width = `${size}px`;
  this.canvas.style.height = `${size}px`;

  // resolução real
  this.canvas.width = Math.round(size * this.dpr);
  this.canvas.height = Math.round(size * this.dpr);

  this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

  // agora o canvas "mundo" é esse tamanho
  this.w = size;
  this.h = size;

  this.portalRadius = this.getPortalRadius();
  this.buildPortalCenters();
  this.buildPortalData();
};

  private drawTriangle(x: number, y: number, size: number, rotation: number, fill: string) {
    this.ctx.save();
    this.ctx.translate(x, y);
    this.ctx.rotate(rotation);
    this.ctx.fillStyle = fill;
    this.ctx.beginPath();
    this.ctx.moveTo(0, -size);
    this.ctx.lineTo(size * 0.82, size * 0.68);
    this.ctx.lineTo(-size * 0.82, size * 0.68);
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.restore();
  }

  private drawBackground() {
    this.ctx.fillStyle = this.CONFIG.bgColor;
    this.ctx.fillRect(0, 0, this.w, this.h);
  }

  private drawPortalAt(c: PortalCenter, portal: PortalData, t: number, index: number) {
    const r = this.portalRadius;
    const phase = c.phase;
    const hue = portal.hue;

    this.drawSoftHalo(c, r, t, phase, hue);
    this.drawPortalImageSoft(c, r, t, phase, hue, portal.img);
    this.drawInnerLines(c, r, t, phase, hue);
    this.drawThinRing(c, r, t, phase, hue);
    this.drawRingSquares(c, t, phase, hue, portal.ringSquares);
    this.drawRingTriangles(c, t, phase, hue, portal.ringTriangles);
    this.drawOuterParticles(c, t, phase, index, portal, portal.particles);
  }

  private drawSoftHalo(c: PortalCenter, r: number, t: number, phase: number, hue: number) {
    const pulse = 1 + Math.sin(t * this.CONFIG.pulseSpeed + phase) * 0.03;
    const g = this.ctx.createRadialGradient(c.x, c.y, r * 0.65, c.x, c.y, r * 1.45 * pulse);

    g.addColorStop(0, this.portalColor(hue, this.CONFIG.haloAlpha, 54));
    g.addColorStop(1, this.portalColor(hue, 0, 42));

    this.ctx.fillStyle = g;
    this.ctx.beginPath();
    this.ctx.arc(c.x, c.y, r * 1.5, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private drawPortalImageSoft(
    c: PortalCenter,
    r: number,
    t: number,
    phase: number,
    hue: number,
    img: HTMLImageElement
  ) {
    if (!(img.complete && img.naturalWidth > 0)) return;

    const innerR = r * this.CONFIG.innerRadiusFactor;

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(c.x, c.y, innerR, 0, Math.PI * 2);
    this.ctx.clip();

    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;
    const target = innerR * 2 * this.CONFIG.portalImageScale;
    const scale = Math.max(target / imgW, target / imgH);

    const drawW = imgW * scale;
    const drawH = imgH * scale;

    const driftX = Math.sin(t * this.CONFIG.driftSpeed + phase) * this.CONFIG.portalImageDrift;
    const driftY = Math.cos(t * this.CONFIG.driftSpeed * 1.2 + phase) * this.CONFIG.portalImageDrift;

    const x = c.x - drawW * 0.5 + driftX;
    const y = c.y - drawH * 0.5 + driftY;

    this.ctx.globalAlpha = this.CONFIG.portalImageOpacity;
    this.ctx.drawImage(img, x, y, drawW, drawH);

    this.ctx.restore();
  }

  private drawInnerLines(c: PortalCenter, r: number, t: number, phase: number, hue: number) {
    const rrBase = r * 0.78;

    this.ctx.save();
    this.ctx.translate(c.x, c.y);

    for (let i = 0; i < 5; i++) {
      const rr = ((i + 1) / 5) * rrBase;
      this.ctx.beginPath();
      this.ctx.strokeStyle = this.portalColor(hue, this.CONFIG.lineAlpha * (0.55 + i * 0.1), 68, -6);
      this.ctx.lineWidth = 1;

      for (let a = 0; a <= Math.PI * 2 + 0.06; a += 0.16) {
        const wobble = Math.sin(a * 4 + t * 0.003 + i + phase) * (1 + i * 0.12);
        const rad = rr + wobble;
        const x = Math.cos(a) * rad;
        const y = Math.sin(a) * rad;

        if (a === 0) this.ctx.moveTo(x, y);
        else this.ctx.lineTo(x, y);
      }

      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  private drawThinRing(c: PortalCenter, r: number, t: number, phase: number, hue: number) {
    this.ctx.beginPath();
    this.ctx.strokeStyle = this.portalColor(hue, 0.62, 65);
    this.ctx.lineWidth = 1.5;
    this.ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
    this.ctx.stroke();

    this.ctx.beginPath();
    this.ctx.strokeStyle = this.portalColor(hue, 0.2, 76);
    this.ctx.lineWidth = 1;
    this.ctx.arc(c.x, c.y, r + 3 + Math.sin(t * 0.002 + phase) * 1.1, 0, Math.PI * 2);
    this.ctx.stroke();
  }

  private drawRingSquares(c: PortalCenter, t: number, phase: number, hue: number, ringSquares: PortalSquare[]) {
    for (const p of ringSquares) {
      const tw = (Math.sin(t * 0.0035 + p.tw + phase) + 1) * 0.5;
      const rr = p.rr + Math.sin(t * 0.002 + p.tw + phase) * 1.5;
      const x = c.x + Math.cos(p.a) * rr;
      const y = c.y + Math.sin(p.a) * rr + Math.sin(t * 0.0018 + p.tw + phase) * p.drift;
      const s = p.size * (0.94 + tw * 0.22);

      this.ctx.fillStyle = this.portalColor(hue, 0.26 + tw * 0.28, 58 + tw * 12, -4);
      this.ctx.fillRect(x - s * 0.5, y - s * 0.5, s, s);
    }
  }

  private drawRingTriangles(c: PortalCenter, t: number, phase: number, hue: number, ringTriangles: PortalTriangle[]) {
    for (const p of ringTriangles) {
      const tw = (Math.sin(t * 0.0037 + p.tw + phase) + 1) * 0.5;
      const rr = p.rr + Math.sin(t * 0.0022 + p.tw + phase) * 1.8;
      const x = c.x + Math.cos(p.a) * rr;
      const y = c.y + Math.sin(p.a) * rr;
      const rot = p.rot + t * this.CONFIG.spinSpeed + phase * 0.15;
      const s = p.size * (0.92 + tw * 0.2);

      this.drawTriangle(x, y, s, rot, this.hotColor(hue, 0.18 + tw * 0.2));
    }
  }

  private drawOuterParticles(
    c: PortalCenter,
    t: number,
    phase: number,
    portalIndex: number,
    portal: PortalData,
    particles: PortalParticle[]
  ) {
    const hue = portal.hue;

    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      const a = p.a + t * p.speed + phase * 0.8 + portalIndex * 0.12;
      const x = c.x + Math.cos(a) * p.dist;
      const y = c.y + Math.sin(a) * p.dist;
      const tw = (Math.sin(t * 0.003 + p.tw + phase) + 1) * 0.5;
      const alpha = 0.08 + tw * 0.14;

      if (p.tri) {
        this.drawTriangle(x, y, p.size, p.rot + t * 0.0008 + phase, this.portalColor(hue, alpha, 62, -8));
      } else {
        this.ctx.fillStyle = this.portalColor(hue, alpha, 60, -5);
        this.ctx.fillRect(x - p.size * 0.5, y - p.size * 0.5, p.size, p.size);
      }
    }
  }

  private animate = (t: number) => {
    this.drawBackground();

    for (let i = 0; i < this.portalCenters.length; i++) {
      this.drawPortalAt(this.portalCenters[i], this.PORTALS[i], t, i);
    }

    requestAnimationFrame(this.animate);
  };
}